import { memo, useEffect, useState } from 'react'

import {
  type V1FormElement,
  type V1FormElementUiSelectorPopupButtons,
  type V1FormElementState,
  type V1FormElementContentState,
  V1FormElementStateType
} from 'mnvm-mypage-app-ts-if'
import { Row } from '../../base/Layout'
import { commonBorder } from '@/styles/border'
import { useApp } from '@/hooks/useApp'
import borderRadius from '@/styles/border_radius'
import spaceStyle from '@/styles/space'
import { fontSize, fontWeight } from '@/styles/font'
import { AccentButton } from '../../base/Button'
import { Block } from '../../base/Block'
import { isEmpty, parseInt } from 'lodash'
import SelectorPopContainer from '@/feature/Builder/Component/containers/SelectorPopupContainer'
import InputErrorLabel from '@/components/base/Form/InputErrorLabel'
import {
  type TJobPostingAdvancedSearchConditionBackup,
  useJobPostingAdvancedSearchConditionBackup,
  EJobPostingAdvancedSearchType
} from '@/hooks/useJobPostingAdvancedSearchConditionBackup'

export interface FormElementSelectorPopupButtonsProps {
  form: V1FormElement
  onChange: (
    newState: V1FormElementState,
    conditionBackup?: TJobPostingAdvancedSearchConditionBackup
  ) => void
  elemenErrorMessage?: string
}

const FormElementSelectorPopupButtons = ({
  form,
  onChange,
  elemenErrorMessage
}: FormElementSelectorPopupButtonsProps): JSX.Element => {
  const app = useApp()
  const [conditionBackup, updateConditionBackup] =
    useJobPostingAdvancedSearchConditionBackup()
  const { ui } = form
  const { buttons } = ui as V1FormElementUiSelectorPopupButtons
  const [popupIndex, setPopupIndex] = useState<number | undefined>(undefined)

  const items = buttons ?? []

  const defaultSelected = (form.state?.contentStates ?? []).map((e) => {
    switch (form.state?.type) {
      case V1FormElementStateType.Uint32:
      case V1FormElementStateType.Uint32List:
        return e.uint32?.toString() ?? ''
      case V1FormElementStateType.Text:
      case V1FormElementStateType.TextList:
        return e.text ?? ''
      case V1FormElementStateType.Bool:
      case V1FormElementStateType.BoolList:
        return e.bool?.toString() ?? ''
      default:
        return ''
    }
  })

  useEffect(() => {
    // update form key to backup when popup is opened
    updateConditionBackup({
      ...conditionBackup,
      areaIdsFormKey: form.state?.key ?? '',
      searchType:
        popupIndex !== undefined
          ? popupIndex === 0
            ? EJobPostingAdvancedSearchType.MUNICIPALITY
            : EJobPostingAdvancedSearchType.TRAINLINE_STATION
          : conditionBackup?.searchType
    })
  }, [popupIndex])

  return (
    <>
      <Row gutter={8}>
        {items.map((e, index) => {
          return (
            <Block key={e.label} style={{ flex: 1 }}>
              <AccentButton
                width="100%"
                style={{
                  boxShadow: 'none',
                  justifyContent: 'left',
                  boxSizing: 'border-box',
                  flex: 'none'
                }}
                padding={{ vertical: spaceStyle.L, horizontal: spaceStyle.M }}
                iconProps={{
                  iconType: e.iconType ?? 'ICON_TYPE_UNSPECIFIED',
                  iconStyle: { theme: app.theme.primaryMainColor }
                }}
                onClick={(event) => {
                  event.preventDefault()
                  setPopupIndex(index)
                }}
                border={{ ...commonBorder, color: app.theme.frameColor }}
                borderRadius={borderRadius.M}
                fontSize={fontSize.S}
                fontWeight={fontWeight.Regular}
                textColor={app.theme.mainTextColor}
              >
                {e.label ?? ''}
              </AccentButton>
            </Block>
          )
        })}
      </Row>
      {popupIndex !== undefined &&
      items[popupIndex].popup?.viewId !== undefined ? (
        <SelectorPopContainer
          viewId={items[popupIndex].popup?.viewId ?? ''}
          onSelect={(items, _, municipalityMap) => {
            const createNewState = (
              item: string
            ): V1FormElementContentState => {
              switch (form.state?.type) {
                case V1FormElementStateType.Uint32:
                case V1FormElementStateType.Uint32List:
                  return { uint32: parseInt(item) }
                case V1FormElementStateType.Text:
                case V1FormElementStateType.TextList:
                  return { text: item }
                case V1FormElementStateType.Bool:
                case V1FormElementStateType.BoolList:
                  return {
                    bool:
                      item.toLowerCase() === 'true' ||
                      item.toLowerCase() === '1'
                  }
                default:
                  break
              }
              return {}
            }

            const contentStates: V1FormElementContentState[] =
              items.map(createNewState)
            const state: V1FormElementState = {
              ...form.state,
              contentStates: contentStates.length === 0 ? [] : contentStates
            }
            onChange(state, {
              ...conditionBackup,
              areaIdsFormKey: form.state?.key,
              municipalityMap: !isEmpty(municipalityMap)
                ? municipalityMap
                : conditionBackup?.municipalityMap
            })
          }}
          defaultTabSelected={popupIndex ?? 0}
          defaultSelected={defaultSelected}
          onClose={() => {
            setPopupIndex(undefined)
          }}
          isOpen={popupIndex !== undefined}
        />
      ) : undefined}
      {elemenErrorMessage !== undefined && (
        <InputErrorLabel message={elemenErrorMessage} />
      )}
    </>
  )
}

export default memo(FormElementSelectorPopupButtons)
